import React from "react";
import { RecAPI } from "@ai360/core";
import { IntlShape, useIntl } from "@ai360/core/intl";
import * as selectors from "../../selectors";
import { DialogBox } from "~/core";
import { RecDetails } from "~/recs-events/recs/model";
import { useAppSelector } from "~/store/hooks";
import { messages } from "./i18n-messages";
import "./batch-edit-recs-modal.css";

interface BatchEditRecsModalProps {
    isOpen: boolean;
    onClose: () => void;
    batchRecDetailsForEdit: RecDetails[];
}

type FormatMessage = IntlShape["formatMessage"];

const getPrimaryEquationSetText = (
    rec: RecDetails,
    equationGroupList: RecAPI.IRecEquationGroup[],
    formatMessage: FormatMessage
): JSX.Element | string => {
    if (!rec) {
        return "";
    }
    const primaryEquationGuid = rec.recAreaList[0].recs[0].equationGroupGuid;
    const equationName = equationGroupList.find((eq) => {
        return eq.equationGroupGuid === primaryEquationGuid;
    })?.groupName;
    const displayEquationName = equationName
        ? equationName
        : formatMessage(messages.unknownEquationText);
    return (
        <div className="primary-rec">
            {rec.name}
            {formatMessage(messages.isUsingText)}
            {displayEquationName}
        </div>
    );
};

const getEquationSetText = (
    batchRecs: RecDetails[],
    equationGroupList: RecAPI.IRecEquationGroup[],
    formatMessage: FormatMessage
): JSX.Element | string => {
    if (!batchRecs || batchRecs.length < 1) {
        return "";
    }
    const primaryEquationGuid = batchRecs[0].recAreaList[0].recs[0].equationGroupGuid;
    const otherEquationNames = [];
    for (const rec of batchRecs) {
        const eqGuid = rec.recAreaList[0].recs[0].equationGroupGuid;
        if (eqGuid !== primaryEquationGuid) {
            const equation = equationGroupList.find((eq) => {
                return eq.equationGroupGuid === eqGuid;
            });
            const equationName = equation
                ? equation.groupName
                : formatMessage(messages.unknownEquationSetText);
            otherEquationNames.push({
                name: rec.name,
                setName: equationName,
                guid: eqGuid,
            });
        }
    }

    return (
        <ul className="secondary-list">
            {otherEquationNames.map((eq) => (
                <li key={eq.setName}>
                    {eq.name}
                    {formatMessage(messages.isUsingText)}
                    {eq.setName}
                </li>
            ))}
        </ul>
    );
};

export const BatchEditRecsModal = (props: BatchEditRecsModalProps): JSX.Element => {
    const { isOpen, onClose, batchRecDetailsForEdit } = props;
    const equationGroupList = useAppSelector(selectors.getEquationGroupList);
    const { formatMessage } = useIntl();
    const msg = formatMessage(messages.sameEquationSetRequiredText);
    const title = formatMessage(messages.batchEditRecsModalTitleText);
    const primaryEquation = getPrimaryEquationSetText(
        batchRecDetailsForEdit[0],
        equationGroupList,
        formatMessage
    );
    const otherEquations = getEquationSetText(
        batchRecDetailsForEdit,
        equationGroupList,
        formatMessage
    );
    return (
        <DialogBox
            draggable
            className="batch-edit-recs-modal"
            forceOverflow
            isOpen={isOpen}
            onClose={() => onClose()}
            title={title}
            unrestricted
            testId="batch-edit-recs-modal"
        >
            {msg}
            {primaryEquation}
            <div>{formatMessage(messages.unmatchingRecsText)}</div>
            {otherEquations}
        </DialogBox>
    );
};

export default BatchEditRecsModal;
